"use client";
import { useEffect, useRef, useState } from "react";
import { NavItem } from "@/monarch/rules/hubRedesign/types";
import useIsMobile from "@/hooks/useIsMobile";
import { StickyNavDesktopLink } from "./StickyNavDesktopLink";
import useTranslations from "@/i18n/useTranslations";

export interface StickyNavDesktopComponentProps {
  navItems?: NavItem[] | null;
}

export const StickyNavDesktop = ({
  navItems = [],
}: StickyNavDesktopComponentProps) => {
  const t = useTranslations("Common");

  const isMobile = useIsMobile();
  const navRef = useRef<HTMLElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [navTop, setNavTop] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current && navTop > 0) {
        const scrollTop = window.scrollY;

        // Use a small buffer to prevent flickering
        if (scrollTop >= navTop - 5) {
          setIsSticky(true);
        } else if (scrollTop < navTop - 10) {
          setIsSticky(false);
        }
      }
    };

    const handleResize = () => {
      if (navRef.current && !isSticky) {
        // Only recalculate position when not sticky to prevent jumping
        const rect = navRef.current.getBoundingClientRect();
        setNavTop(rect.top + window.scrollY);
      }
    };

    // Set initial position only once
    if (navRef.current && navTop === 0) {
      const rect = navRef.current.getBoundingClientRect();
      setNavTop(rect.top + window.scrollY);
    }

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [navTop, isSticky]);

  if (isMobile) {
    return null;
  }

  return (
    <>
      {/* Placeholder div to maintain layout when nav becomes fixed */}
      {isSticky && <div className="h-[76px]" />}

      <nav
        ref={navRef}
        aria-label={t("pageSections")}
        className={`
          w-screen h-[76px] min-h-[76px] shadow-md
          flex items-center justify-center
          bg-secondary transition-shadow duration-300 ease-in-out
          ${isSticky ? "fixed top-0 left-0 right-0 z-50 shadow-lg" : "relative"}
        `}
        style={{
          marginLeft: isSticky ? "0" : "calc(-50vw + 50%)",
          marginRight: isSticky ? "0" : "calc(-50vw + 50%)",
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center justify-between flex-1 mx-auto mx-3">
            {navItems?.map((item: NavItem, index: number) => (
              <StickyNavDesktopLink item={item} index={index} key={index} />
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};
